import React, { useState, useEffect } from 'react';
import OrgFeaturesPanel from "../../components/admin/OrgFeaturesPanel";
import api from '../../api';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';
import { UploadCloud, Sparkles, ShieldCheck, RefreshCw, CheckCircle2, XCircle, Clock, Loader2, FileText, AlertTriangle, MoreVertical, X, Building2, Ban, Eye, Pencil, RotateCcw } from 'lucide-react';
import { createPortal } from 'react-dom';

import { validateOrgTaxIds, normalizeTaxId } from '../../utils/taxIdValidation';
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Kept in sync with the backend's own CYCLE_DAYS_BY_BILLING_CYCLE
// (app/billing/subscriptions.py) - must match exactly, or a renewal
// computed here would drift from what the backend actually grants/charges.
const CYCLE_DAYS_BY_BILLING_CYCLE = { monthly: 30, quarterly: 90, half_yearly: 182, yearly: 365 };
const cycleDaysFor = (cycle) => CYCLE_DAYS_BY_BILLING_CYCLE[cycle] ?? 30;

const addDaysISO = (dateStr, days) => {
  const d = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  return toISODate(d);
};

// Renewal dates: the new cycle starts right where the previous one ended
// (not from today) - same "extend from the old end_date" semantics
// extend_subscription() already uses server-side after a real payment.
// A brand-new org (no previousEndDate) starts from today instead.
const computeRenewalDates = (cycle, previousEndDate) => {
  const start = previousEndDate || toISODate(new Date());
  return { start, end: addDaysISO(start, cycleDaysFor(cycle)) };
};

// Per-session AI billing defaults for a new org (training BE ai_billing.py):
// monthly AI add-on unless switched; Rs 50/session; AI pauses at 10 unpaid.
const BLANK_AI_FORM = { mode: 'monthly', price_per_session: 50, max_unpaid_sessions: 10 };

const BLANK_SUB_FORM = {
  plan_id: '',
  billing_cycle: 'monthly',
  base_price_per_cycle: '',
  ai_addon_enabled: false,
  ai_addon_price_per_cycle: '',
  ai_sessions_per_day_per_seat: '',
  seat_limit: '',
  status: 'active',
  start_date: '',
  end_date: '',
};

// Hidden per product request (kept for easy re-enable) - uploading the
// cancelled cheque is now treated as sufficient assurance on its own, so
// the Razorpay penny-drop "Verify Account" flow is redundant for now.
// Replaced below by an explicit payout warning + consent checkbox instead.
const SHOW_ACCOUNT_VERIFICATION = false;

export default function AdminOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  // Individual org: its admin login is also the practitioner's doctor profile.
  const [specializationList, setSpecializationList] = useState([]);
  useEffect(() => {
    api.fetchSpecializations()
      .then((res) => setSpecializationList(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, []);
  const [locations, setLocations] = useState({ states: [], cities: [] });
  const [loading, setLoading] = useState(true);
  // Deactivated orgs drop out of the default list (backend's
  // get_all_organizations() only returns is_active=True unless asked
  // otherwise) - this toggle lets an admin find one to reactivate.
  const [includeInactive, setIncludeInactive] = useState(false);
  // Row actions menu - same 3-dot/portal/fixed-position pattern as
  // pages/actors/doctor/Doctor.js's handleMenuToggle, kept consistent
  // across table pages rather than inline text-link actions per row.
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const handleMenuToggle = (event, orgId) => {
    if (menuOpen === orgId) {
      setMenuOpen(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    const top = rect.bottom + 4;
    const estimatedHeight = 100;
    const adjustedTop = (top + estimatedHeight > window.innerHeight && rect.top > estimatedHeight)
      ? rect.top - estimatedHeight - 4
      : top;
    setMenuPosition({ top: adjustedTop, right });
    setMenuOpen(orgId);
  };

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    address: '',
    phone_number: '',
    email_id: '',
    state_id: '',
    city_id: '',
    org_type: 1,
    gstin: '',
    pan_number: '',
    uploader_url: '',
    uploader_encryption_key: '',
    service_api_key: '',
    client_key: '',
    openai_key: '',
    gemini_key: '',
    gcp_project_id: 'mindconnect-1765015416867',
    // AI setup (ai/ai_credentials.py): 'shared' = NL Labs' AI, read live
    // from the platform org (never copied); 'own' = the keys below.
    ai_source: 'shared',
    ai_can_share: true,
    gcp_location: 'asia-south1',
    bank_account_holder_name: '',
    bank_account_number: '',
    confirm_bank_account_number: '',
    bank_ifsc_code: '',
    bank_name: '',
    bank_branch: '',
    bank_account_type: 'current',
    cheque_image_key: ''
  });

  // Server-reported state for the current org being edited - display-only,
  // separate from formData since it's never something the admin types.
  const [bankMeta, setBankMeta] = useState({
    bank_account_number_last4: null,
    bank_details_saved: false,
    cheque_image_url: null,
    bank_verification_status: 'not_verified',
    bank_verification_message: null,
    bank_verification_checked_at: null,
  });
  const [chequeUploading, setChequeUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [chequeSuggestion, setChequeSuggestion] = useState(null);
  // Set only when Apply to Fields finds a manually-entered value that
  // conflicts with what the cheque extraction read - holds off applying
  // until the admin explicitly confirms which value to keep, rather than
  // silently overwriting a manual entry that might be correct while the
  // AI read of a smudged/handwritten cheque is wrong.
  const [chequeDiscrepancies, setChequeDiscrepancies] = useState(null);
  // Re-affirmed on every save that actually sets/changes the account
  // number (see bankDetailsBeingSet below) - deliberately not inherited
  // from a previous save, even when editing an org that already has bank
  // details on file, since consent should be a fresh explicit action each
  // time the payout account is being set.
  const [bankDetailsConsent, setBankDetailsConsent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Onboard/Edit is now two tabs - Basic (everything above) and
  // Subscription (license terms) - so an admin assigning a subscription at
  // onboarding time doesn't have to leave this screen and go find a
  // separate one (there wasn't one). Submit saves both in one action: the
  // org first (create or update), then the subscription PUT against
  // whichever org_id that produced.
  const [activeTab, setActiveTab] = useState('basic');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  // The org's prior subscription end_date, if any (null for a brand-new
  // org/onboarding) - the anchor a renewal's new cycle starts from, so
  // picking a billing cycle extends from where the last cycle actually
  // ended rather than from whatever happens to be in Start Date.
  const [previousEndDate, setPreviousEndDate] = useState(null);
  const [subForm, setSubForm] = useState(BLANK_SUB_FORM);
  const [aiForm, setAiForm] = useState(BLANK_AI_FORM);
  // One AI choice in the form, stored in two places (see the AI Features block).
  // If an org somehow has both on, per-session wins: it's the explicit billing mode.
  const aiChoice = aiForm.mode === 'per_session' ? 'per_session' : (subForm.ai_addon_enabled ? 'addon' : 'none');
  const setAiChoice = (choice) => {
    setSubForm(prev => ({ ...prev, ai_addon_enabled: choice === 'addon' }));
    setAiForm(prev => ({ ...prev, mode: choice === 'per_session' ? 'per_session' : 'monthly' }));
  };
  const [aiUsage, setAiUsage] = useState(null);

  // "View Details" (context menu) - read-only combined Basic + Subscription
  // view, separate from the edit form so viewing never risks an accidental
  // field change.
  const [viewingOrg, setViewingOrg] = useState(null);
  const [viewingSubscription, setViewingSubscription] = useState(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);

  useEffect(() => {
    fetchData();
    api.fetchSubscriptionPlans()
      .then(res => setSubscriptionPlans(res.data || []))
      .catch(() => {}); // catalog is a convenience prefill, not required
  }, [includeInactive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [orgsRes, locsRes] = await Promise.all([
        api.getOrganizations(includeInactive),
        api.getLocations()
      ]);
      setOrganizations(orgsRes.data || []);
      setLocations(locsRes.data || { states: [], cities: [] });
    } catch (err) {
      toast.error('Failed to load organizations or locations');
    } finally {
      setLoading(false);
    }
  };

  // Deactivating an org is the highest-blast-radius action on this page -
  // every user under it immediately loses the ability to log in (see
  // login()'s org.is_active check, app/routes/auth/auth.py) - so this
  // asks for explicit confirmation naming the org, same as any other
  // destructive action in this app.
  const handleDeactivate = async (org) => {
    if (!window.confirm(`Deactivate "${org.name}"? Every user in this organization will immediately be unable to log in. This can be undone later.`)) {
      return;
    }
    try {
      await api.deactivateOrganization(org.id);
      toast.success(`${org.name} deactivated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate organization');
    }
  };

  const handleReactivate = async (org) => {
    try {
      await api.reactivateOrganization(org.id);
      toast.success(`${org.name} reactivated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reactivate organization');
    }
  };

  // Read-only combined Basic + Subscription view (context menu "View
  // Details") - deliberately a separate modal from the Edit form, so
  // looking something up never risks an accidental field change.
  const handleViewDetails = (org) => {
    setViewingOrg(org);
    setViewingSubscription(null);
    setViewDetailsLoading(true);
    api.fetchOrgSubscription(org.id)
      .then(res => setViewingSubscription(res.data || { has_subscription: false }))
      .catch(() => setViewingSubscription({ has_subscription: false }))
      .finally(() => setViewDetailsLoading(false));
  };

  const handleEdit = (org) => {
    setFormData({
      id: org.id,
      name: org.name || '',
      address: org.address || '',
      phone_number: org.phone_number || '',
      email_id: org.email_id || '',
      state_id: org.state_id || '',
      city_id: org.city_id || '',
      org_type: org.org_type || 1,
      gstin: org.gstin || '',
      pan_number: org.pan_number || '',
      uploader_url: org.uploader_url || '',
      uploader_encryption_key: org.uploader_encryption_key || '',
      service_api_key: org.service_api_key || '',
      client_key: org.client_key || '',
      openai_key: org.openai_key || '',
      gemini_key: org.gemini_key || '',
      gcp_project_id: org.gcp_project_id || '',
      gcp_location: org.gcp_location || 'asia-south1',
      ai_source: org.ai_source || 'own',
      ai_can_share: org.ai_can_share !== false,
      // Full account number is never returned by the server - stays blank
      // here, same "leave blank to keep unchanged" convention as the other
      // secret fields (uploader_encryption_key, service_api_key, ...).
      bank_account_holder_name: org.bank_account_holder_name || '',
      bank_account_number: '',
      confirm_bank_account_number: '',
      bank_ifsc_code: org.bank_ifsc_code || '',
      bank_name: org.bank_name || '',
      bank_branch: org.bank_branch || '',
      bank_account_type: org.bank_account_type || 'current',
      cheque_image_key: org.cheque_image_key || ''
    });
    setBankMeta({
      bank_account_number_last4: org.bank_account_number_last4 || null,
      bank_details_saved: !!org.bank_details_saved,
      cheque_image_url: org.cheque_image_url || null,
      bank_verification_status: org.bank_verification_status || 'not_verified',
      bank_verification_message: org.bank_verification_message || null,
      bank_verification_checked_at: org.bank_verification_checked_at || null,
    });
    setChequeSuggestion(null);
    setChequeDiscrepancies(null);
    setBankDetailsConsent(false);
    setActiveTab('basic');
    setHasExistingSubscription(false);
    setPreviousEndDate(null);
    setSubForm(BLANK_SUB_FORM);
    setAiForm(BLANK_AI_FORM);
    setAiUsage(null);
    api.fetchOrgAiBilling(org.id)
      .then(res => {
        const d = res.data || {};
        setAiForm({ mode: d.mode || 'monthly', price_per_session: d.price_per_session ?? 50, max_unpaid_sessions: d.max_unpaid_sessions ?? 10 });
        setAiUsage(d);
      })
      .catch(() => {});
    api.fetchOrgSubscription(org.id)
      .then(res => {
        const d = res.data || {};
        if (d.has_subscription) {
          setHasExistingSubscription(true);
          setPreviousEndDate(d.end_date || null);
          setSubForm({
            plan_id: d.plan_id || '',
            billing_cycle: d.billing_cycle || 'monthly',
            base_price_per_cycle: d.base_price_per_cycle ?? '',
            ai_addon_enabled: !!d.ai_addon_enabled,
            ai_addon_price_per_cycle: d.ai_addon_price_per_cycle ?? '',
            ai_sessions_per_day_per_seat: d.ai_sessions_per_day_per_seat ?? '',
            seat_limit: d.seat_limit ?? '',
            status: d.status || 'active',
            start_date: d.start_date || '',
            end_date: d.end_date || '',
          });
        }
      })
      .catch(() => {}); // no existing subscription is a normal, expected case
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.bank_account_number.trim() !== formData.confirm_bank_account_number.trim()) {
      toast.error('Account Number and Confirm Account Number do not match');
      return;
    }
    if (formData.bank_ifsc_code && !IFSC_REGEX.test(formData.bank_ifsc_code.toUpperCase())) {
      toast.error('IFSC Code format looks invalid (expected e.g. HDFC0001234)');
      return;
    }
    // Bank account number is mandatory - blank means "keep unchanged" on
    // an existing org (see its placeholder hint), which is only valid if
    // this org already has one on file. A new org, or one with neither a
    // new value nor an existing one, must provide it.
    // Self-signed-up orgs add their own (Settings -> Payout Bank Details).
    if (formData.bank_account_number.trim() === '' && !bankMeta.bank_details_saved && !isSelfSignupOrg) {
      toast.error('Bank account number is required.');
      return;
    }
    // Account Number blank means "keep unchanged" on an existing org (see
    // its placeholder hint) - consent is only required when the payout
    // account is actually being set/changed this save, not on every
    // unrelated edit to an org that already has bank details on file.
    if (formData.bank_account_number.trim() !== '' && !bankDetailsConsent) {
      toast.error('Please confirm the bank details are correct before saving.');
      return;
    }

    if (taxIdErrors.gstin || taxIdErrors.pan) {
      toast.error(taxIdErrors.gstin || taxIdErrors.pan);
      setActiveTab('basic');
      return;
    }

    if (!subscriptionTabValid) {
      toast.error('Please complete the Subscription tab before saving.');
      setActiveTab('subscription');
      return;
    }

    const { confirm_bank_account_number, ...rest } = formData;
    // bankDetailsConsent lives in its own component state, not formData -
    // the backend records who/when consented (organization/organization_routes.py)
    // whenever this is true alongside a non-empty bank_account_number.
    const payload = { ...rest, bank_details_consent: bankDetailsConsent };
    let orgId = formData.id;
    try {
      if (formData.id) {
        await api.updateOrganization(formData.id, payload);
        toast.success('Organization updated successfully');
      } else {
        const res = await api.createOrganization(payload);
        orgId = res.data.id;
        toast.success('Organization created successfully');
        // welcome_email_sent/admin_user_created come from the backend's
        // own attempt (organization/organization_routes.py) - surfaced here since
        // that step can fail independently of org creation itself (e.g.
        // no email entered, SMTP not configured, email already in use).
        if (res.data.admin_user_created && res.data.welcome_email_sent) {
          toast.success('Welcome email sent to the organization admin');
        } else if (res.data.admin_user_created && !res.data.welcome_email_sent) {
          toast.warn('Admin account created, but the welcome email failed to send - check SMTP configuration.');
        } else if (!formData.email_id) {
          toast.info('No email entered - admin account and welcome email were skipped. You can add one later and re-save.');
        } else {
          toast.warn('Admin account was not auto-created (that email may already be in use by another account).');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save organization');
      return;
    }

    // Subscription is saved as a second step against whichever org_id the
    // step above produced - a failure here is reported separately rather
    // than rolled back, since the org record itself already saved fine and
    // the admin can just reopen Edit and retry the Subscription tab alone.
    try {
      const subPayload = {
        ...subForm,
        // Per-session AI replaces the monthly add-on (one AI choice in the form).
        ai_addon_enabled: aiChoice === 'addon',
        base_price_per_cycle: Number(subForm.base_price_per_cycle),
        ai_addon_price_per_cycle: subForm.ai_addon_price_per_cycle === '' ? null : Number(subForm.ai_addon_price_per_cycle),
        ai_sessions_per_day_per_seat: subForm.ai_sessions_per_day_per_seat === '' ? null : Number(subForm.ai_sessions_per_day_per_seat),
        seat_limit: subForm.seat_limit === '' ? null : Number(subForm.seat_limit),
        plan_id: subForm.plan_id === '' ? null : subForm.plan_id,
        start_date: subForm.start_date || undefined,
      };
      await api.updateOrgSubscription(orgId, subPayload);
      toast.success('Subscription saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Organization saved, but the subscription could not be saved - reopen Edit to retry.');
    }

    try {
      await api.updateOrgAiBilling(orgId, {
        mode: aiForm.mode,
        price_per_session: Number(aiForm.price_per_session),
        max_unpaid_sessions: Number(aiForm.max_unpaid_sessions),
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Organization saved, but AI billing could not be saved - reopen Edit to retry.');
    }

    setShowForm(false);
    fetchData();
  };

  const generateKey = (type) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    // Generate a random string (approx matching Fernet for client_key or hex for service_api)
    if (type === 'client_key') {
        // Fernet is 32 url-safe base64 encoded bytes (44 chars)
        for (let i = 0; i < 43; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        result += '=';
        setFormData({...formData, uploader_encryption_key: result});
    } else {
        // Hex for service_api
        const hexChars = '0123456789abcdef';
        for (let i = 0; i < 32; i++) {
            result += hexChars.charAt(Math.floor(Math.random() * hexChars.length));
        }
        setFormData({...formData, service_api_key: result});
    }
  };

  const handleChequeFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      toast.error('File size exceeds the 1MB limit.');
      e.target.value = '';
      return;
    }
    setChequeUploading(true);
    setChequeSuggestion(null);
    setChequeDiscrepancies(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.uploadBankCheque(fd);
      setFormData(prev => ({ ...prev, cheque_image_key: res.data.cheque_image_key }));
      setBankMeta(prev => ({ ...prev, cheque_image_url: res.data.preview_url }));
      toast.success('Cheque image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cheque upload failed');
    } finally {
      setChequeUploading(false);
      e.target.value = '';
    }
  };

  const handleExtractFromCheque = async () => {
    if (!formData.cheque_image_key) return;
    setExtracting(true);
    try {
      const res = await api.extractChequeDetails(formData.cheque_image_key);
      setChequeSuggestion(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cheque extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  // Fields compared between what the admin already typed and what the
  // cheque extraction read. Account number is included even though its
  // input is masked (type="password") - formData still holds the real
  // plain value, only the on-screen rendering is masked.
  const CHEQUE_FIELD_MAP = [
    { key: 'bank_account_holder_name', suggestionKey: 'account_holder_name', label: 'Account Holder' },
    { key: 'bank_account_number', suggestionKey: 'account_number', label: 'Account Number' },
    { key: 'bank_ifsc_code', suggestionKey: 'ifsc_code', label: 'IFSC Code' },
    { key: 'bank_name', suggestionKey: 'bank_name', label: 'Bank Name' },
    { key: 'bank_branch', suggestionKey: 'branch', label: 'Branch' },
  ];

  const findChequeDiscrepancies = (suggestion) => {
    const discrepancies = [];
    for (const { key, suggestionKey, label } of CHEQUE_FIELD_MAP) {
      const entered = (formData[key] || '').trim();
      const extracted = (suggestion[suggestionKey] || '').trim();
      // Only a real conflict when BOTH sides actually have a value -
      // an empty manual field isn't a "discrepancy", it's just unfilled.
      if (!entered || !extracted) continue;
      if (entered.toLowerCase() !== extracted.toLowerCase()) {
        discrepancies.push({ label, entered, extracted });
      }
    }
    return discrepancies;
  };

  const applyChequeSuggestionValues = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      bank_account_holder_name: suggestion.account_holder_name || prev.bank_account_holder_name,
      bank_account_number: suggestion.account_number || prev.bank_account_number,
      confirm_bank_account_number: suggestion.account_number || prev.confirm_bank_account_number,
      bank_ifsc_code: suggestion.ifsc_code || prev.bank_ifsc_code,
      bank_name: suggestion.bank_name || prev.bank_name,
      bank_branch: suggestion.branch || prev.bank_branch,
    }));
    setChequeSuggestion(null);
    setChequeDiscrepancies(null);
    toast.success('Suggested details applied - please review before saving');
  };

  const handleApplySuggestion = () => {
    if (!chequeSuggestion) return;
    const discrepancies = findChequeDiscrepancies(chequeSuggestion);
    if (discrepancies.length > 0) {
      // Hold off applying - surface exactly what conflicts so the admin
      // can decide which value is actually correct, instead of silently
      // overwriting a manual entry with a possibly-misread cheque value.
      setChequeDiscrepancies(discrepancies);
      return;
    }
    applyChequeSuggestionValues(chequeSuggestion);
  };

  const handleApplyChequeAnyway = () => {
    if (!chequeSuggestion) return;
    applyChequeSuggestionValues(chequeSuggestion);
  };

  const handleVerifyAccount = async () => {
    if (!formData.id) return;
    setVerifying(true);
    try {
      const res = await api.verifyBankAccount(formData.id);
      setBankMeta(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed to start');
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckVerificationStatus = async () => {
    if (!formData.id) return;
    setVerifying(true);
    try {
      const res = await api.getBankVerificationStatus(formData.id);
      setBankMeta(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not check status');
    } finally {
      setVerifying(false);
    }
  };

  const availableCities = locations.cities.filter(
    c => !formData.state_id || c.state_id.toString() === formData.state_id.toString()
  );

  // Live mismatch check (not just on submit) - Account Number is masked
  // below, so the Confirm field is the only place the admin can actually
  // see what they typed; a silent toast that's easy to miss isn't enough
  // feedback when they can't visually compare the two fields themselves.
  const accountNumberMismatch =
    formData.bank_account_number.trim() !== '' &&
    formData.confirm_bank_account_number.trim() !== '' &&
    formData.bank_account_number.trim() !== formData.confirm_bank_account_number.trim();

  const bankDetailsBeingSet = formData.bank_account_number.trim() !== '';
  // Self-signed-up orgs add their own bank account (Settings -> Payout Bank Details).
  const isSelfSignupOrg = !!(formData.id && organizations.find((o) => o.id === formData.id)?.is_self_signup);

  // Basic tab validity - mirrors the `required` attributes already on the
  // fields below, computed here too so the single Submit button (which now
  // saves both tabs) can be disabled until the whole thing is actually
  // fillable, not just until the Basic section's own native HTML
  // validation would pass.
  const taxIdErrors = validateOrgTaxIds(formData.org_type, formData.gstin, formData.pan_number);
  const isCorporate = Number(formData.org_type) === 2;

  const basicTabValid =
    !taxIdErrors.gstin &&
    !taxIdErrors.pan &&
    formData.name.trim() !== '' &&
    formData.phone_number.trim() !== '' &&
    formData.email_id.trim() !== '' &&
    formData.address.trim() !== '' &&
    formData.state_id !== '' &&
    formData.city_id !== '' &&
    !accountNumberMismatch &&
    (bankMeta.bank_details_saved || bankDetailsBeingSet || isSelfSignupOrg) &&
    (!bankDetailsBeingSet || bankDetailsConsent);

  // Subscription tab validity.
  const subscriptionTabValid =
    subForm.billing_cycle !== '' &&
    String(subForm.base_price_per_cycle).trim() !== '' &&
    Number(subForm.base_price_per_cycle) >= 0 &&
    subForm.status !== '' &&
    subForm.end_date !== '' &&
    (!subForm.ai_addon_enabled || (String(subForm.ai_addon_price_per_cycle).trim() !== '' && Number(subForm.ai_addon_price_per_cycle) >= 0));

  const canSubmitOrgForm = basicTabValid && subscriptionTabValid;

  // Catalog prices (subscription_plans.default_base_price /
  // default_ai_addon_price) are PER SEAT, not a flat org price - an org
  // with 5 seats owes 5x what a 1-seat org on the same plan does. Picking a
  // plan multiplies by whatever Seat Limit is already filled in (1 if
  // blank/unlimited) as a one-time starting point; it's never a lock-in -
  // every computed field stays a normal editable input afterward, and nothing
  // here re-multiplies automatically if Seat Limit changes later (see the
  // "Recalculate" suggestion rendered next to Base Price below instead -
  // explicit, one click, never silently overwrites a manual edit).
  const selectedPlan = subscriptionPlans.find(p => String(p.id) === String(subForm.plan_id));
  const seatsForPricing = Number(subForm.seat_limit) || 1;
  const planBaseSuggestion = selectedPlan ? round2(Number(selectedPlan.default_base_price || 0) * seatsForPricing) : null;
  const planAddonSuggestion = selectedPlan?.default_ai_addon_price != null
    ? round2(Number(selectedPlan.default_ai_addon_price) * seatsForPricing)
    : null;

  const handlePlanSelect = (planId) => {
    const plan = subscriptionPlans.find(p => String(p.id) === String(planId));
    if (!plan) {
      setSubForm(prev => ({ ...prev, plan_id: '' }));
      return;
    }
    const seats = Number(subForm.seat_limit) || 1;
    // Same recompute the Billing Cycle dropdown's own onChange does - a plan
    // carries its own billing_cycle, so picking one changes the cycle just
    // as much as picking it directly from that dropdown does, and Start/End
    // Date need to follow for the same reason.
    const { start, end } = computeRenewalDates(plan.billing_cycle, previousEndDate);
    setSubForm(prev => ({
      ...prev,
      plan_id: plan.id,
      billing_cycle: plan.billing_cycle,
      start_date: start,
      end_date: end,
      base_price_per_cycle: plan.default_base_price != null ? round2(Number(plan.default_base_price) * seats) : prev.base_price_per_cycle,
      ai_addon_price_per_cycle: plan.default_ai_addon_price != null ? round2(Number(plan.default_ai_addon_price) * seats) : prev.ai_addon_price_per_cycle,
      ai_sessions_per_day_per_seat: plan.ai_sessions_per_day_per_seat ?? prev.ai_sessions_per_day_per_seat,
    }));
  };

  // Pressing Enter in a text/select field moves to the next control instead
  // of submitting the form early - this form has ~25 fields across 3
  // sections, so Enter-to-submit from, say, the Organization Name field
  // would be a frequent mis-submit. Textareas keep Enter's normal
  // newline behavior, and buttons keep their normal click-on-Enter.
  const focusNextOnEnter = (e) => {
    const tag = e.target.tagName;
    if (e.key !== 'Enter' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
    e.preventDefault();
    const focusable = Array.from(
      e.currentTarget.querySelectorAll('input, select, textarea, button')
    ).filter(el => !el.disabled && el.tabIndex !== -1 && el.offsetParent !== null);
    const index = focusable.indexOf(e.target);
    const next = focusable[index + 1];
    if (next) next.focus();
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8">
      {!showForm && (
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Organizations</h1>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} className="rounded border-gray-300" />
              Show deactivated
            </label>
          <button
            onClick={() => {
              setFormData({ id: null, name: '', address: '', phone_number: '', email_id: '', state_id: '', city_id: '', org_type: 1, gstin: '', pan_number: '', uploader_url: '', uploader_encryption_key: '', service_api_key: '', client_key: '', openai_key: '', gemini_key: '', gcp_project_id: 'mindconnect-1765015416867', gcp_location: 'asia-south1', ai_source: 'shared', ai_can_share: true, bank_account_holder_name: '', bank_account_number: '', confirm_bank_account_number: '', bank_ifsc_code: '', bank_name: '', bank_branch: '', bank_account_type: 'current', cheque_image_key: '' });
              setBankMeta({ bank_account_number_last4: null, bank_details_saved: false, cheque_image_url: null, bank_verification_status: 'not_verified', bank_verification_message: null, bank_verification_checked_at: null });
              setChequeSuggestion(null);
              setChequeDiscrepancies(null);
              setBankDetailsConsent(false);
              setActiveTab('basic');
              setHasExistingSubscription(false);
              setPreviousEndDate(null);
              setSubForm(BLANK_SUB_FORM);
              setAiForm(BLANK_AI_FORM);
              setAiUsage(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Onboard Organization
          </button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : showForm ? (
        <div className="max-w-6xl mx-auto space-y-4">
          <div>
            <button type="button" onClick={() => setShowForm(false)} className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Organizations</span>
            </button>
          </div>

          {/* Label lives inside the panel now (same pattern as /doctor-edit)
              rather than floating above it as its own page-level heading. */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{formData.id ? 'Edit Organization' : 'New Organization'}</h2>
                <p className="text-xs text-gray-400">Fields marked <span className="text-red-500">*</span> are required</p>
              </div>
            </div>

            {/* Two tabs: Basic (org record) and Subscription (license terms) -
                both save together on a single Submit below, so an onboarding
                admin can't accidentally skip assigning a subscription. */}
            <div className="flex gap-2 border-b border-gray-200 px-6">
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'basic' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                1. Basic {basicTabValid && <CheckCircle2 className="inline w-4 h-4 ml-1 text-green-500" />}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('subscription')}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'subscription' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                2. Subscription {subscriptionTabValid && <CheckCircle2 className="inline w-4 h-4 ml-1 text-green-500" />}
              </button>
              {formData.id && (
                <button
                  type="button"
                  onClick={() => setActiveTab('features')}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'features' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                >
                  3. Features
                </button>
              )}
            </div>

          <form onSubmit={handleSubmit} onKeyDown={focusNextOnEnter} className="p-6 space-y-6">

            {activeTab === 'features' && formData.id && <OrgFeaturesPanel orgId={formData.id} />}

            {activeTab === 'basic' && (
            <>
            {/* SECTION 1: Basic Details */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">1. Basic Details</h3>
              <p className="text-sm text-gray-500 mb-6">General information and location mapping for the organization.</p>
              
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Name *</label>
                    <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">The legal or display name of the clinic or organization.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization Type *</label>
                    <select required value={formData.org_type} onChange={e => setFormData({...formData, org_type: parseInt(e.target.value)})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2">
                      <option value={1}>Individual</option>
                      <option value={2}>Corporate</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">1 for individual practitioners, 2 for corporate hospitals/clinics.</p>
                  </div>
                  {!formData.id && Number(formData.org_type) === 1 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Practitioner's Specialization</label>
                      <select value={formData.specialization || ''} onChange={e => setFormData({...formData, specialization: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2">
                        <option value="">Choose (they can set it later)</option>
                        {specializationList.map((sp) => {
                          const name = typeof sp === 'string' ? sp : sp.name;
                          return <option key={name} value={name}>{name}</option>;
                        })}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">The admin login doubles as the practitioner's doctor profile (one seat). They add their schedule, degree and bio under Clinicians.</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                    <input required type="text" value={formData.phone_number} onChange={e => setFormData({...formData, phone_number: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                    <input required type="email" value={formData.email_id} onChange={e => setFormData({...formData, email_id: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">{formData.id ? "This organization's registered contact email." : 'Required to auto-create the org admin account and send the welcome email.'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Address *</label>
                  <textarea required value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" rows="2"></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State *</label>
                    <select required value={formData.state_id} onChange={e => setFormData({...formData, state_id: e.target.value, city_id: ''})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2">
                      <option value="">Select State</option>
                      {locations.states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City *</label>
                    <select required value={formData.city_id} onChange={e => setFormData({...formData, city_id: e.target.value})} disabled={!formData.state_id} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 disabled:bg-gray-100">
                      <option value="">Select City</option>
                      {availableCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Used for filtering and search features in the app.</p>
                  </div>
                </div>

                {/* Tax identity: Corporate -> GSTIN required; Individual ->
                    PAN required, GSTIN optional (utils/taxIdValidation.js,
                    enforced again server-side). */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GSTIN{isCorporate ? ' *' : ''}</label>
                    <input type="text" maxLength={15} value={formData.gstin} onChange={e => setFormData({...formData, gstin: normalizeTaxId(e.target.value)})} className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 font-mono uppercase ${formData.gstin && taxIdErrors.gstin ? 'border-red-400' : 'border-gray-300'}`} placeholder="15-character GSTIN" />
                    {(formData.gstin || formData.id) && taxIdErrors.gstin
                      ? <p className="text-xs text-red-600 mt-1">{taxIdErrors.gstin}</p>
                      : <p className="text-xs text-gray-500 mt-1">{isCorporate ? 'Required for a Corporate organization.' : 'Optional for an Individual practitioner - fill in only if GST-registered.'}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">PAN{!isCorporate ? ' *' : ''}</label>
                    <input type="text" maxLength={10} value={formData.pan_number} onChange={e => setFormData({...formData, pan_number: normalizeTaxId(e.target.value)})} className={`mt-1 block w-full rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 font-mono uppercase ${formData.pan_number && taxIdErrors.pan ? 'border-red-400' : 'border-gray-300'}`} placeholder="e.g. ABCDE1234F" />
                    {(formData.pan_number || formData.id) && taxIdErrors.pan
                      ? <p className="text-xs text-red-600 mt-1">{taxIdErrors.pan}</p>
                      : <p className="text-xs text-gray-500 mt-1">{isCorporate ? 'Optional - taken from the GSTIN if left blank.' : 'Required for an Individual practitioner.'}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: System Use Keys */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">2. System Use Keys & Integrations</h3>
              <p className="text-sm text-gray-500 mb-6">Cloud infrastructure and encrypted API keys for this organization. <span className="font-semibold text-red-500">Note: Saved keys are securely encrypted in the database and cannot be viewed later.</span></p>
              
              {formData.ai_can_share && (
                <fieldset className="mb-6">
                  <legend className="block text-sm font-medium text-gray-700 mb-2">AI setup</legend>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ['shared', "Use NL Labs' AI", "Session analysis, drafts and pre-reviews run on NL Labs' AI keys. Nothing is copied here: change the key once in NL Labs' own organisation and every clinic on this option uses the new one."],
                      ['own', 'Own keys', "The clinic's own Uploader and AI keys, entered below. Needed when the clinic hosts its own Uploader."],
                    ].map(([value, title, hint]) => (
                      <label key={value} className={`normal-case font-sans tracking-normal flex gap-3 rounded-lg border-2 p-3 cursor-pointer ${formData.ai_source === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                        <input type="radio" name="ai_source" value={value} checked={formData.ai_source === value} onChange={() => setFormData({ ...formData, ai_source: value })} className="mt-1" />
                        <span><span className="block text-sm font-semibold text-gray-900">{title}</span><span className="block text-xs text-gray-600 mt-0.5">{hint}</span></span>
                      </label>
                    ))}
                  </div>
                  {formData.id && formData.ai_source === 'own' && <p className="mt-2 text-xs text-amber-700">Make sure this clinic has its own Uploader address and AI keys below, or its AI features will stop working.</p>}
                </fieldset>
              )}

              {(formData.ai_source === 'own' || !formData.ai_can_share) && (<>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 font-semibold mb-1">Key Generation Tips:</p>
                    <ul className="list-disc list-inside text-sm text-blue-600 space-y-1">
                      <li>Use the <strong>Generate</strong> buttons to create secure keys here, allowing you to copy them before saving.</li>
                      <li>Alternatively, you can manually paste your own pre-generated keys.</li>
                      <li>If left completely blank during creation, the system will auto-generate them behind the scenes.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GCP Project ID</label>
                    <input type="text" value={formData.gcp_project_id} onChange={e => setFormData({...formData, gcp_project_id: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 bg-white" />
                    <p className="text-xs text-gray-500 mt-1">Google Cloud project ID if hosting separately.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GCP Location</label>
                    <input type="text" value={formData.gcp_location} onChange={e => setFormData({...formData, gcp_location: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 bg-white" />
                    <p className="text-xs text-gray-500 mt-1">GCP Server location (e.g. asia-south1).</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uploader URL (Client ID)</label>
                    <input type="text" value={formData.uploader_url} onChange={e => setFormData({...formData, uploader_url: e.target.value})} placeholder="http://host.docker.internal:5002/api" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">URL of client-hosted services. Leave blank if managed by us.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uploader Encryption Key</label>
                    <div className="flex mt-1">
                      <input type="text" value={formData.uploader_encryption_key} onChange={e => setFormData({...formData, uploader_encryption_key: e.target.value})} placeholder={formData.id ? "Leave blank to keep unchanged" : ""} className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                      <button type="button" onClick={() => generateKey('client_key')} className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700">Generate</button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">The Fernet key used to encrypt the data before sending to the client URL.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service API Key</label>
                    <div className="flex mt-1">
                      <input type="text" value={formData.service_api_key} onChange={e => setFormData({...formData, service_api_key: e.target.value})} placeholder={formData.id ? "Leave blank to keep unchanged" : ""} className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                      <button type="button" onClick={() => generateKey('service_api')} className="px-4 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700">Generate</button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">The bearer token key used to authenticate communications with the client API.</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">OpenAI API Key</label>
                      <input type="password" value={formData.openai_key} onChange={e => setFormData({...formData, openai_key: e.target.value})} placeholder={formData.id ? "Leave blank to keep unchanged" : ""} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                      <p className="text-xs text-gray-500 mt-1">Used if the org brings their own OpenAI key.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Gemini API Key</label>
                      <input type="password" value={formData.gemini_key} onChange={e => setFormData({...formData, gemini_key: e.target.value})} placeholder={formData.id ? "Leave blank to keep unchanged" : ""} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                      <p className="text-xs text-gray-500 mt-1">Used if the org brings their own Google Gemini key.</p>
                    </div>
                  </div>
                </div>
              </div>
              </>)}
            </div>

            {/* SECTION 3: Bank Details & Payout */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">3. Bank Details &amp; Payout</h3>
              <p className="text-sm text-gray-500 mb-6">Where payouts are sent. <span className="font-semibold text-red-500">The account number is encrypted and cannot be viewed later - only the last 4 digits are shown.</span></p>

              <div className="space-y-6">
                {/* Cancelled Cheque Upload + AI Extraction - first control in
                    this section: upload/extract before manual entry, so the
                    admin can let the cheque fill the fields below rather
                    than typing first and discovering a conflict later. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cancelled Cheque (Image or PDF)</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 cursor-pointer">
                      <UploadCloud className="w-4 h-4" />
                      {chequeUploading ? 'Uploading...' : 'Choose File'}
                      <input type="file" accept="image/png,image/jpeg,image/jpg,application/pdf,.pdf" className="hidden" disabled={chequeUploading} onChange={handleChequeFileChange} />
                    </label>
                    {bankMeta.cheque_image_url && (
                      formData.cheque_image_key?.toLowerCase().endsWith('.pdf') ? (
                        <a href={bankMeta.cheque_image_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-14 px-3 rounded border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100">
                          <FileText className="w-5 h-5 text-red-500" />
                          View PDF
                        </a>
                      ) : (
                        <img src={bankMeta.cheque_image_url} alt="Cancelled cheque preview" className="h-14 rounded border border-gray-200" />
                      )
                    )}
                    {formData.cheque_image_key && (
                      <button
                        type="button"
                        onClick={handleExtractFromCheque}
                        disabled={extracting}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-md text-sm font-semibold disabled:opacity-60"
                      >
                        {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {extracting ? 'Reading cheque...' : '✨ Extract from Cheque'}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Max 1MB. AI-read suggestion only - always reviewed by you before it's applied to the fields below.</p>

                  {chequeSuggestion && (
                    <div className="mt-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <p className="text-sm font-semibold text-indigo-900 mb-2">Suggested from cheque image:</p>
                      <ul className="text-sm text-indigo-800 space-y-0.5 mb-3">
                        <li>Account Holder: {chequeSuggestion.account_holder_name || <em className="text-indigo-400">not legible</em>}</li>
                        <li>Account Number: {chequeSuggestion.account_number || <em className="text-indigo-400">not legible</em>}</li>
                        <li>IFSC: {chequeSuggestion.ifsc_code || <em className="text-indigo-400">not legible</em>}</li>
                        <li>Bank: {chequeSuggestion.bank_name || <em className="text-indigo-400">not legible</em>}</li>
                        <li>Branch: {chequeSuggestion.branch || <em className="text-indigo-400">not legible</em>}</li>
                      </ul>

                      {chequeDiscrepancies ? (
                        <div className="p-3 bg-amber-50 border border-amber-300 rounded-md">
                          <div className="flex items-start gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm font-semibold text-amber-900">This doesn't match what's already entered below:</p>
                          </div>
                          <ul className="text-sm text-amber-800 space-y-1 mb-3">
                            {chequeDiscrepancies.map(d => (
                              <li key={d.label}>
                                <span className="font-semibold">{d.label}:</span> you entered "{d.entered}", cheque shows "{d.extracted}"
                              </li>
                            ))}
                          </ul>
                          <div className="flex gap-2">
                            <button type="button" onClick={handleApplyChequeAnyway} className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-xs font-semibold hover:bg-amber-700">Use Cheque Values</button>
                            <button type="button" onClick={() => { setChequeSuggestion(null); setChequeDiscrepancies(null); }} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-semibold text-gray-600 hover:bg-gray-50">Keep My Entries</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button type="button" onClick={handleApplySuggestion} className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-semibold hover:bg-indigo-700">Apply to Fields</button>
                          <button type="button" onClick={() => { setChequeSuggestion(null); setChequeDiscrepancies(null); }} className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-semibold text-gray-600 hover:bg-gray-50">Dismiss</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-gray-100 pt-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Holder Name</label>
                    <input type="text" value={formData.bank_account_holder_name} onChange={e => setFormData({...formData, bank_account_holder_name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Type</label>
                    <select value={formData.bank_account_type} onChange={e => setFormData({...formData, bank_account_type: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2">
                      <option value="current">Current</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Number {!bankMeta.bank_details_saved && '*'}</label>
                    <input type="password" autoComplete="new-password" value={formData.bank_account_number} onChange={e => setFormData({...formData, bank_account_number: e.target.value})} placeholder={bankMeta.bank_details_saved ? `Leave blank to keep unchanged (•••• ${bankMeta.bank_account_number_last4 || '----'})` : ''} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">Masked while typing - re-type it in Confirm Account Number below to verify.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Confirm Account Number</label>
                    <input type="text" value={formData.confirm_bank_account_number} onChange={e => setFormData({...formData, confirm_bank_account_number: e.target.value})} className={`mt-1 block w-full rounded-md shadow-sm border p-2 ${accountNumberMismatch ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} />
                    {accountNumberMismatch && (
                      <p className="text-xs text-red-600 mt-1 font-medium">Account numbers do not match.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                    <input type="text" value={formData.bank_ifsc_code} onChange={e => setFormData({...formData, bank_ifsc_code: e.target.value.toUpperCase()})} placeholder="e.g. HDFC0001234" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 uppercase" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                    <input type="text" value={formData.bank_name} onChange={e => setFormData({...formData, bank_name: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Branch</label>
                    <input type="text" value={formData.bank_branch} onChange={e => setFormData({...formData, bank_branch: e.target.value})} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                  </div>
                </div>

                {/* Payout warning + consent - replaces the Razorpay
                    penny-drop "Account Verification" flow below for now;
                    uploading the cancelled cheque is treated as
                    sufficient assurance on its own. */}
                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                    <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-900">
                      This will be the account where payouts are sent. Please double-check every field above (account number, IFSC, bank name) is correct before saving - a wrong account number can delay or misdirect a payout.
                    </p>
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bankDetailsConsent}
                      onChange={e => setBankDetailsConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm the bank details entered above are correct, and I agree to NL Labs' terms and conditions governing payouts to this account.
                    </span>
                  </label>
                </div>

                {SHOW_ACCOUNT_VERIFICATION && (
                  <div className="border-t border-gray-100 pt-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Account Verification:</span>
                        {bankMeta.bank_verification_status === 'verified' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                          </span>
                        )}
                        {bankMeta.bank_verification_status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            <Clock className="w-3.5 h-3.5" /> Pending
                          </span>
                        )}
                        {bankMeta.bank_verification_status === 'failed' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                            <XCircle className="w-3.5 h-3.5" /> Failed
                          </span>
                        )}
                        {bankMeta.bank_verification_status === 'not_verified' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                            Not Verified
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleVerifyAccount}
                          disabled={!formData.id || verifying}
                          title={!formData.id ? 'Save the organization first' : ''}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-md text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                          Verify Account
                        </button>
                        {bankMeta.bank_verification_status === 'pending' && (
                          <button
                            type="button"
                            onClick={handleCheckVerificationStatus}
                            disabled={!formData.id || verifying}
                            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <RefreshCw className="w-4 h-4" /> Check Status
                          </button>
                        )}
                      </div>
                    </div>
                    {!formData.id && (
                      <p className="text-xs text-gray-400 mt-1">Save the organization once first - verification needs a saved record to check against.</p>
                    )}
                    {bankMeta.bank_verification_message && (
                      <p className={`text-xs mt-2 ${bankMeta.bank_verification_status === 'failed' ? 'text-rose-600' : 'text-gray-600'}`}>
                        {bankMeta.bank_verification_message}
                        {bankMeta.bank_verification_checked_at && (
                          <span className="text-gray-400"> · checked {new Date(bankMeta.bank_verification_checked_at).toLocaleString()}</span>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setActiveTab('subscription')}
                className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium shadow-sm"
              >
                Next: Subscription →
              </button>
            </div>
            </>
            )}

            {activeTab === 'subscription' && (
            <>
            {/* SECTION 4: Subscription */}
            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">2. Subscription</h3>
              <p className="text-sm text-gray-500 mb-6">
                License terms for this organization{hasExistingSubscription ? ' (editing the existing subscription)' : ' - required before this org can be saved'}.
              </p>

              <div className="space-y-5">
                {subscriptionPlans.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Select A Plan</label>
                    <select
                      value={subForm.plan_id}
                      onChange={e => handlePlanSelect(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    >
                      <option value="">Custom (no preset)</option>
                      {subscriptionPlans.map(p => (
                        <option key={p.id} value={p.id}>{p.name} - ₹{p.default_base_price}/seat/{p.billing_cycle} + ₹{p.default_ai_addon_price}/seat add-on</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Catalog prices are per seat - picking a plan multiplies by Seat Limit below (1 if left unlimited) to prefill Base Price and AI Add-on Price. Every field stays a normal editable input afterward, so you can override the computed total for this org at any time.</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Billing Cycle *</label>
                    <select
                      required
                      value={subForm.billing_cycle}
                      onChange={e => {
                        const cycle = e.target.value;
                        const { start, end } = computeRenewalDates(cycle, previousEndDate);
                        setSubForm(prev => ({ ...prev, billing_cycle: cycle, start_date: start, end_date: end }));
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half_yearly">Half-yearly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Also recomputes Start/End Date below{previousEndDate ? ` (from the previous cycle's end, ${previousEndDate})` : ''} - still freely editable after.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status *</label>
                    <select required value={subForm.status} onChange={e => setSubForm({ ...subForm, status: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2">
                      <option value="active">Active</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Base Price / Cycle (₹) *</label>
                    <input required type="number" min="0" step="0.01" value={subForm.base_price_per_cycle} onChange={e => setSubForm({ ...subForm, base_price_per_cycle: e.target.value })} onWheel={e => e.target.blur()} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    {selectedPlan && planBaseSuggestion !== null && Number(subForm.base_price_per_cycle) !== planBaseSuggestion ? (
                      <p className="text-xs text-amber-600 mt-1">
                        ₹{selectedPlan.default_base_price}/seat × {seatsForPricing} seat{seatsForPricing === 1 ? '' : 's'} = ₹{planBaseSuggestion}.{' '}
                        <button type="button" onClick={() => setSubForm(prev => ({ ...prev, base_price_per_cycle: planBaseSuggestion }))} className="underline font-semibold hover:text-amber-700">
                          Use this
                        </button> (or keep your own override).
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">Total for this org, charged every cycle - override the plan's per-seat x Seat Limit total freely.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Seat Limit</label>
                    <input type="number" min="0" value={subForm.seat_limit} onChange={e => setSubForm({ ...subForm, seat_limit: e.target.value })} onWheel={e => e.target.blur()} placeholder="Blank = unlimited" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">Individual orgs are hard-capped at 1 seat regardless of this value. Changing this does not auto-recalculate price - see the suggestion next to Base Price.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      value={subForm.start_date}
                      onChange={e => {
                        const start = e.target.value;
                        setSubForm(prev => ({
                          ...prev,
                          start_date: start,
                          end_date: start ? addDaysISO(start, cycleDaysFor(prev.billing_cycle)) : prev.end_date,
                        }));
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Blank defaults to today on a brand-new subscription. Changing this also recomputes End Date.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date *</label>
                    <input required type="date" value={subForm.end_date} onChange={e => setSubForm({ ...subForm, end_date: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                    <p className="text-xs text-gray-500 mt-1">Auto-set from Billing Cycle/Start Date above - still a normal field, edit it directly whenever you need an exact different date.</p>
                  </div>
                </div>

                {/* AI features: one choice. "addon" = subscription's monthly AI add-on
                    (subForm.ai_addon_enabled); "per_session" = OrgAiBillingModel mode
                    per_session (aiForm). Both are still saved by their own APIs. */}
                <div className="border-t border-gray-100 pt-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">AI Features</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {[
                      { value: 'none', title: 'Not included', text: 'Practice management only.' },
                      { value: 'addon', title: 'Monthly AI add-on', text: 'Fixed price per cycle, daily session allowance.' },
                      { value: 'per_session', title: 'Pay per AI session', text: 'Fee per session, taken from payouts.' },
                    ].map(opt => (
                      <label key={opt.value} className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${aiChoice === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="ai_choice" className="mt-1" checked={aiChoice === opt.value} onChange={() => setAiChoice(opt.value)} />
                        <span>
                          <span className="block text-sm font-semibold text-gray-800">{opt.title}</span>
                          <span className="block text-xs text-gray-500">{opt.text}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {aiChoice === 'addon' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">AI Add-on Price / Cycle (₹) *</label>
                        <input required type="number" min="0" step="0.01" value={subForm.ai_addon_price_per_cycle} onChange={e => setSubForm({ ...subForm, ai_addon_price_per_cycle: e.target.value })} onWheel={e => e.target.blur()} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                        {selectedPlan && planAddonSuggestion !== null && Number(subForm.ai_addon_price_per_cycle) !== planAddonSuggestion && (
                          <p className="text-xs text-amber-600 mt-1">
                            ₹{selectedPlan.default_ai_addon_price}/seat × {seatsForPricing} = ₹{planAddonSuggestion}.{' '}
                            <button type="button" onClick={() => setSubForm(prev => ({ ...prev, ai_addon_price_per_cycle: planAddonSuggestion }))} className="underline font-semibold hover:text-amber-700">
                              Use this
                            </button>
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">AI Sessions / Day / Seat</label>
                        <input type="number" min="0" value={subForm.ai_sessions_per_day_per_seat} onChange={e => setSubForm({ ...subForm, ai_sessions_per_day_per_seat: e.target.value })} onWheel={e => e.target.blur()} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                      </div>
                    </div>
                  )}
                  {aiChoice === 'per_session' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Price per AI session (₹)</label>
                          <input type="number" min="0" value={aiForm.price_per_session} onChange={e => setAiForm({ ...aiForm, price_per_session: e.target.value })} onWheel={e => e.target.blur()} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Pause AI after unpaid sessions</label>
                          <input type="number" min="1" value={aiForm.max_unpaid_sessions} onChange={e => setAiForm({ ...aiForm, max_unpaid_sessions: e.target.value })} onWheel={e => e.target.blur()} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" />
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Each AI session's fee is taken from the org's payouts. Fees with no online payment to deduct from become dues it pays from Finance, and AI pauses once unpaid sessions reach the limit.
                      </p>
                    </>
                  )}
                  {aiUsage?.mode === 'per_session' && (
                    <p className="mt-3 text-sm text-gray-600">
                      Current usage: {aiUsage.pending_count} unpaid session(s) (₹{aiUsage.pending_amount}), of which {aiUsage.dues_count} are dues (₹{aiUsage.dues_amount}).
                      {aiUsage.ai_paused && <span className="ml-1 font-semibold text-red-600">AI is paused for this org.</span>}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-start pt-2">
              <button
                type="button"
                onClick={() => setActiveTab('basic')}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium"
              >
                ← Back: Basic
              </button>
            </div>
            </>
            )}

            <div className="flex justify-end space-x-4 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium">Cancel</button>
              <button
                type="submit"
                disabled={!canSubmitOrgForm}
                title={!canSubmitOrgForm ? 'Complete both the Basic and Subscription tabs first' : ''}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                Save Organization
              </button>
            </div>
          </form>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizations.map(org => {
                const state = locations.states.find(s => s.id === org.state_id);
                const city = locations.cities.find(c => c.id === org.city_id);
                return (
                  <tr key={org.id} className={org.is_active === false ? 'opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{org.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">{city?.name ? `${city.name}, ` : ''}{state?.name || 'Not set'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">{org.phone_number || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.is_active === false ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Deactivated</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                      <button
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                        onClick={(e) => handleMenuToggle(e, org.id)}
                        data-label="Organization Options"
                        title="Actions"
                      >
                        <MoreVertical size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Row actions menu - portal + fixed position, same pattern as Doctor.js */}
      {menuOpen && (() => {
        const selectedOrg = organizations.find(o => o.id === menuOpen);
        if (!selectedOrg) return null;
        return createPortal(
          <div
            className="fixed bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-[160px] py-1.5"
            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800"
              onClick={() => { setMenuOpen(null); handleViewDetails(selectedOrg); }}
              data-label="View Organization Details"
            >
              <Eye size={14} /> View Details
            </button>
            <button
              className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800"
              onClick={() => { setMenuOpen(null); handleEdit(selectedOrg); }}
              data-label="Edit Organization"
            >
              <Pencil size={14} /> Edit
            </button>
            {selectedOrg.is_active === false ? (
              <button
                className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-green-50 transition-colors text-sm font-medium text-green-700"
                onClick={() => { setMenuOpen(null); handleReactivate(selectedOrg); }}
                data-label="Reactivate Organization"
              >
                <RotateCcw size={14} /> Reactivate
              </button>
            ) : (
              <button
                className="flex items-center gap-2 w-full text-left px-4 py-2 hover:bg-red-50 transition-colors text-sm font-medium text-red-600"
                onClick={() => { setMenuOpen(null); handleDeactivate(selectedOrg); }}
                data-label="Deactivate Organization"
              >
                <Ban size={14} /> Deactivate
              </button>
            )}
          </div>,
          document.body
        );
      })()}

      {/* Click outside to close menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(null)} />
      )}

      {/* View Details modal - read-only Basic + Subscription summary */}
      {viewingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setViewingOrg(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-gray-800">{viewingOrg.name}</h3>
              <button onClick={() => setViewingOrg(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Basic Details</h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div><dt className="text-gray-500">Type</dt><dd className="font-medium text-gray-900">{viewingOrg.org_type === 2 ? 'Corporate' : 'Individual'}</dd></div>
                  <div><dt className="text-gray-500">Status</dt><dd className="font-medium text-gray-900">{viewingOrg.is_active === false ? 'Deactivated' : 'Active'}</dd></div>
                  <div><dt className="text-gray-500">Phone</dt><dd className="font-medium text-gray-900">{viewingOrg.phone_number || '-'}</dd></div>
                  <div><dt className="text-gray-500">Email</dt><dd className="font-medium text-gray-900">{viewingOrg.email_id || '-'}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-gray-500">Address</dt><dd className="font-medium text-gray-900">{viewingOrg.address || '-'}</dd></div>
                  <div><dt className="text-gray-500">Location</dt><dd className="font-medium text-gray-900">
                    {locations.cities.find(c => c.id === viewingOrg.city_id)?.name
                      ? `${locations.cities.find(c => c.id === viewingOrg.city_id).name}, `
                      : ''}
                    {locations.states.find(s => s.id === viewingOrg.state_id)?.name || 'Not set'}
                  </dd></div>
                  <div><dt className="text-gray-500">GSTIN</dt><dd className="font-medium text-gray-900">{viewingOrg.gstin || 'Not registered'}</dd></div>
                  <div><dt className="text-gray-500">PAN</dt><dd className="font-medium text-gray-900">{viewingOrg.pan_number || 'Not set'}</dd></div>
                </dl>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Subscription</h4>
                {viewDetailsLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : !viewingSubscription?.has_subscription ? (
                  <p className="text-sm text-gray-500">No subscription assigned to this organization yet.</p>
                ) : (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div><dt className="text-gray-500">Billing Cycle</dt><dd className="font-medium text-gray-900 capitalize">{String(viewingSubscription.billing_cycle || "").replace(/_/g, "-")}</dd></div>
                    <div><dt className="text-gray-500">Status</dt><dd className="font-medium text-gray-900 capitalize">{viewingSubscription.status}</dd></div>
                    <div><dt className="text-gray-500">Base Price / Cycle</dt><dd className="font-medium text-gray-900">₹{Number(viewingSubscription.base_price_per_cycle || 0).toFixed(2)}</dd></div>
                    <div><dt className="text-gray-500">Seat Limit</dt><dd className="font-medium text-gray-900">{viewingSubscription.seat_limit ?? 'Unlimited'}</dd></div>
                    <div><dt className="text-gray-500">AI Add-on</dt><dd className="font-medium text-gray-900">
                      {viewingSubscription.ai_addon_enabled
                        ? `Enabled - ₹${Number(viewingSubscription.ai_addon_price_per_cycle || 0).toFixed(2)}/cycle, ${viewingSubscription.ai_sessions_per_day_per_seat || 0} sessions/day/seat`
                        : 'Disabled'}
                    </dd></div>
                    <div><dt className="text-gray-500">Start Date</dt><dd className="font-medium text-gray-900">{viewingSubscription.start_date || '-'}</dd></div>
                    <div><dt className="text-gray-500">End Date</dt><dd className="font-medium text-gray-900">{viewingSubscription.end_date || '-'}</dd></div>
                  </dl>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setViewingOrg(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium text-sm">Close</button>
              <button onClick={() => { const org = viewingOrg; setViewingOrg(null); handleEdit(org); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
